import { useEffect, useState } from 'react'

import { MultiSelect } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { REQUEST_PACKAGES, UPDATE_PACKAGE } from 'src/graphql/packages.graphql'
import { PackageType } from 'src/graphql/types/package'
import { StepType } from 'src/graphql/types/steps'
import { formatSnakeValue } from 'src/lib/formatters'

const stepOptions = Object.values(StepType).map((v) => ({
  value: v,
  label: formatSnakeValue(v),
}))

interface StepsCellProps {
  pkg: {
    packageId: string
    packageType: PackageType
    stepType: string[]
  }
}

const StepsCell = ({ pkg }: StepsCellProps) => {
  // Package is keyed by packageId, not id, and has no keyFields in the Apollo
  // cache config, so the mutation result never merges into the packages list.
  // Hold the selection locally so the row reflects the change straight away,
  // and refetch the list so every derived view catches up.
  const [localSteps, setLocalSteps] = useState(pkg.stepType)

  useEffect(() => {
    setLocalSteps(pkg.stepType)
  }, [pkg.stepType])

  const [updatePackage] = useMutation(UPDATE_PACKAGE, {
    onCompleted: ({ updatePackage: result }) =>
      toast(`${result.name} updated`, 'success'),
    onError: (e) => {
      setLocalSteps(pkg.stepType)
      toast(e?.message || 'Unable to update package', 'error')
    },
    refetchQueries: [REQUEST_PACKAGES],
  })

  const missingAsurintSteps =
    pkg.packageType === PackageType.ASURINT && !localSteps.length

  return (
    <MultiSelect
      data={stepOptions}
      variant="unstyled"
      size="xs"
      value={localSteps}
      error={
        missingAsurintSteps
          ? 'Asurint orders will fail until steps are added'
          : undefined
      }
      onChange={(val) => {
        setLocalSteps(val)
        updatePackage({
          variables: { packageId: pkg.packageId, input: { stepType: val } },
        })
      }}
    />
  )
}

export default StepsCell
